import {filter, map} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Timer, Table, TableHead, TableBody, TableHeadCell, TableCell, TableRow, Icon, ParticipantPlatform,
    ParticipantInfo, CallButtonProps
} from 'components';
import {OnlineSession, AccountAssignmentInfo, AnyParticipant} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {ParticipantRole} from 'types';
import {sessionUtils} from 'utils';

const MIN_ROWS_COUNT = 15;
const TABLE_HEADER_KEYS = TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE.TABLE.HEADER;

type Props = {
    sessions: OnlineSession[],
    getStatusOptions: (session: OnlineSession) => ({
        name: string;
        className: string;
    });
    guestEnabled?: boolean;
    renderAssignment: ({groupId, subgroupId}: AccountAssignmentInfo) => React.ReactNode;
    renderActivityDataIcon: (session: OnlineSession) => React.ReactNode;
    renderPreSessionFlowBadge: (session: OnlineSession) => React.ReactNode;
    renderAnswerButton: (session: OnlineSession, buttonProps?: CallButtonProps, defaultNode?: React.ReactNode) => React.ReactNode;
    openParticipantsModal: (sessionId: string) => void;
    onOpenPrevClientSessionsModal: (session: OnlineSession) => void;
};

export const SessionsTable: React.FC<Props> = ({
    sessions, guestEnabled, onOpenPrevClientSessionsModal, getStatusOptions, renderActivityDataIcon,
    renderPreSessionFlowBadge, renderAssignment, openParticipantsModal, renderAnswerButton
}: Props) => {
    const {t} = useTranslation();

    const emptyRows = useMemo(() => {
        const emptyRowsCount = MIN_ROWS_COUNT - (sessions ? sessions.length : 0);
        return new Array(Math.max(emptyRowsCount, 0)).fill(null);
    }, [sessions]);

    const inviteesHeader = useMemo(() => guestEnabled ? t(TABLE_HEADER_KEYS.INVITEES) : t(TABLE_HEADER_KEYS.CLIENT), [guestEnabled, t]);

    const getInviteeList = useCallback((session: OnlineSession) => {
        const invitees = filter(session.participants, (p) => sessionUtils.isInviteeParticipant(p) && !p.removed);
        return sessionUtils.sortParticipantList(invitees);
    }, []);

    const renderPrevClientSessions = useCallback((session: OnlineSession) => (
        session.prevClientSessionsCount > 0 &&
            <div
                className='tw-flex tw-items-center tw-cursor-pointer'
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenPrevClientSessionsModal(session);
                }}
            >
                <Icon
                    button
                    name='rotate'
                    color='default-dimmed'
                    size='sm'
                    margin={false}
                    thickness='sm'
                />
                <span className='tw-text-primary tw-text-nowrap tw-ml-0.5'>
                    {session.prevClientSessionsCount}
                </span>
            </div>
    ), [onOpenPrevClientSessionsModal]);

    const renderInvitee = useCallback((session: OnlineSession, participant: AnyParticipant) => {
        if (!participant) {
            return null;
        }

        const participantData = participant.data;

        return (
            <div className='tw-flex tw-items-center tw-flex-wrap tw-text-break-word'>
                <div className='tw-flex tw-items-center tw-flex-1 2xl:tw-flex-1.5 xl:tw-mr-2'>
                    <ParticipantInfo
                        spacing={false}
                        session={session}
                        participant={participant}
                        fields={{clientInfoModal: true, onlineStatus: true}}
                    />
                </div>

                <div className='2xl:tw-mr-2 -xl:tw-hidden'>
                    <div className='tw-flex tw-items-center tw-w-10'>
                        <ParticipantPlatform participant={participant} />
                    </div>
                </div>
                <div className='-2xl:tw-hidden tw-flex-1 tw-text-default tw-mr-2'>
                    {participantData?.location}
                </div>
                <div className='tw-flex tw-items-center -2xl:tw-w-full -2xl:tw-mt-1 -2xl:tw-ml-3.5'>
                    <div className='2xl:tw-hidden tw-flex-1 tw-text-default xl:tw-mr-2'>
                        {participantData?.location}
                    </div>

                    <div className='tw-w-10 -xl:tw-hidden'>
                        {renderActivityDataIcon(session)}
                    </div>

                    <div className='-xl:tw-hidden tw-mx-2 tw-flex tw-items-center'>
                        {participant.role === ParticipantRole.Client && renderPreSessionFlowBadge(session)}
                    </div>

                    <div className='tw-w-10 -xl:tw-hidden'>
                        {participant.role === ParticipantRole.Client && renderPrevClientSessions(session)}
                    </div>
                </div>
                <div className='xl:tw-hidden tw-flex tw-ml-3.5 tw-mt-1'>
                    <div className='tw-flex tw-items-center tw-w-10'>
                        <ParticipantPlatform participant={participant} />
                    </div>
                    <div className='tw-w-10 tw-ml-4'>
                        {renderActivityDataIcon(session)}
                    </div>

                    <div className='tw-ml-4 tw-flex tw-items-center'>
                        {participant.role === ParticipantRole.Client && renderPreSessionFlowBadge(session)}
                    </div>

                    <div className='tw-w-10 tw-ml-4'>
                        {participant.role === ParticipantRole.Client && renderPrevClientSessions(session)}
                    </div>
                </div>
            </div>
        );
    }, [renderActivityDataIcon, renderPreSessionFlowBadge, renderPrevClientSessions]);

    const renderAnswerButtonInternal = useCallback((session: OnlineSession) => {
        const status = getStatusOptions(session);
        return renderAnswerButton(session, {}, <div className={status.className}>{status.name}</div>);
    }, [getStatusOptions, renderAnswerButton]);

    return (
        <div className='tw-overflow-x-auto'>
            <Table className='tw-min-w-196'>
                <TableHead>
                    <TableRow light>
                        <TableHeadCell>{inviteesHeader}</TableHeadCell>
                        <TableHeadCell className='2xl:tw-w-72'>{t(TABLE_HEADER_KEYS.USERS)}</TableHeadCell>
                        <TableHeadCell className='2xl:tw-w-64'>{t(TABLE_HEADER_KEYS.ASSIGNMENT)}</TableHeadCell>
                        <TableHeadCell className='tw-w-24'>{t(TABLE_HEADER_KEYS.TIME)}</TableHeadCell>
                        <TableHeadCell className='tw-w-36'>{t(TABLE_HEADER_KEYS.STATUS)}</TableHeadCell>
                        <TableHeadCell className='tw-w-12' />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {map(sessions, (session: OnlineSession) => (
                        <TableRow key={session.id} className='tw-h-10'>
                            <TableCell>
                                {map(getInviteeList(session), (participant) => renderInvitee(session, participant))}
                            </TableCell>
                            <TableCell>
                                {map(session.participants, (participant) => {
                                    if (sessionUtils.isInviteeParticipant(participant)) {
                                        return null;
                                    }
                                    if (participant.removed) {
                                        return null;
                                    }
                                    return (
                                        <ParticipantInfo
                                            key={participant.id}
                                            spacing={false}
                                            session={session}
                                            participant={participant}
                                            fields={{onlineStatus: true, platform: true}}
                                            local={sessionUtils.isParticipantLocalUser(participant)}
                                        />
                                    );
                                })}
                            </TableCell>
                            <TableCell><div className='tw-text-break-word'>{renderAssignment(session.assignment)}</div></TableCell>
                            <TableCell>
                                <Timer
                                    config={{optionalHours: true, minutes: true, seconds: true}}
                                    startDate={session.startDate}
                                    endDate={session.endDate}
                                />
                            </TableCell>
                            <TableCell noYPadding>{renderAnswerButtonInternal(session)}</TableCell>
                            <TableCell>
                                <Icon
                                    name='more-horizontal'
                                    color='default-dimmed'
                                    button
                                    onClick={() => openParticipantsModal(session.id)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                    {map(emptyRows, (row, index) => (
                        <TableRow key={index} className='tw-h-10'>
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};
